import { AdUnit } from '@pubstack/common/src/adunit'
import { AdUnitConfig, AdunitDeviceRuleConfig, LazyLoadingRuleConfig, RoadblockType } from '@pubstack/common/src/stack'
import { FunctionComponent, useEffect, useMemo } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { Tooltip } from '~/components/Tooltip'
import { Skeleton } from '~/components/Skeleton'
import { StepperIndicator } from '~/components/StepperIndicator'
import { useToast } from '~/components/Toast/useToasts'
import { useCustomStep } from '~/hooks/useCustomStep'
import { PureAdmEditPage as EditPage } from '~/modules/adm/PureAdmEditPage'
import { PureStackAdUnitSelectionStep } from './PureStackAdUnitSelectionStep'
import { PureStackHeaderBiddingRulesStep } from './PureStackHeaderBiddingRulesStep'
import { PureStackRulesStep } from './PureStackRulesStep'
import { CreateAdmStack, isAdmStack, isStackNameReserved, Stack } from '@pubstack/common/src/stack'
import { toBulkUpdate, useBulkUpdateStacks, useCreateStack, useStack, useStacks } from '~/api/stacks.api'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useAdUnits } from '~/api/adm/ad-units.api'
import { useContexts } from '~/api/adm/contexts.api'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { useSite } from '~/api/app/sites.api'
import { useAtomValue } from 'jotai'
import { currentScopeState } from '~/state'
import { useBidderRules } from '~/api/bidder-rules.api'
import { useLazyLoadingRules } from '~/api/adm/lazy-loading-rules.api'
import { useRefreshRules } from '~/api/adm/refresh-rules.api'
import { isDefaultBidderRuleFor } from '@pubstack/common/src/bidder-rule'
import { pruneFloorRuleConfigToAdUnits, pruneRefreshRulesToAdUnits, resolveAdUnitIds, resolveFloorRuleConfig } from './stackRulePruning'

export type StackFormData = {
  name: CreateAdmStack['name']
  commitMessage: CreateAdmStack['commitMessage']
  configuration: {
    contextId: CreateAdmStack['configuration']['contextId']
    desktopAdUnits: AdUnitConfig[]
    mobileAdUnits: AdUnitConfig[]
    headerBiddingEnabled: CreateAdmStack['configuration']['headerBiddingEnabled']
    adCallTimeout?: CreateAdmStack['configuration']['adCallTimeout']
    lazyLoadingRuleConfig?: LazyLoadingRuleConfig[]
    floorRuleConfig?: CreateAdmStack['configuration']['floorRuleConfig']
    mobileFloorRuleConfig?: CreateAdmStack['configuration']['mobileFloorRuleConfig']
    refreshRules?: AdunitDeviceRuleConfig[]
    bidderRuleId: CreateAdmStack['configuration']['bidderRuleId']
  }
}

const fromStackToForm = (stack: Stack, adUnits: AdUnit[]): StackFormData => {
  if (isAdmStack(stack)) {
    const desktopOutOfRoadblock: AdUnitConfig[] = stack.configuration.desktopAdUnitsIds?.map((id) => ({ adUnitId: id })) ?? []
    const desktopRoadblock: AdUnitConfig[] =
      stack.configuration.desktopRoadblockAdUnitsIds?.map((id, index) => ({ adUnitId: id, roadblock: index === 0 ? RoadblockType.Master : RoadblockType.Companion })) ?? []
    const mobileOutOfRoadblock: AdUnitConfig[] = stack.configuration.mobileAdUnitsIds?.map((id) => ({ adUnitId: id })) ?? []
    const mobileRoadblock: AdUnitConfig[] =
      stack.configuration.mobileRoadblockAdUnitsIds?.map((id, index) => ({ adUnitId: id, roadblock: index === 0 ? RoadblockType.Master : RoadblockType.Companion })) ?? []
    const refreshRules = stack.configuration.refreshRuleConfig
      ?.map((ruleConfig) => ({ ...ruleConfig, ruleId: ruleConfig.ruleId || 'NO_RULE' }))
      .reduce((acc, current) => {
        const ruleIndex = acc.findIndex((r) => r.ruleId === current.ruleId && r.device === current.device)
        const adUnitName = adUnits.find((a) => a.id === current.adUnitId)?.name ?? ''
        if (ruleIndex !== -1) {
          acc[ruleIndex].adUnitIds.push(adUnitName)
        } else {
          acc.push({
            ruleId: current.ruleId || 'NO_RULE',
            device: current.device,
            adUnitIds: [adUnitName],
          })
        }
        return acc
      }, [] as AdunitDeviceRuleConfig[])
    const lazyLoadingRuleConfig = stack.configuration.lazyLoadingRuleConfig?.map((lazyLoadingRule) => ({
      ...lazyLoadingRule,
      ruleId: lazyLoadingRule.ruleId || 'NO_RULE',
    }))
    return {
      name: stack.name,
      commitMessage: '',
      configuration: {
        ...stack.configuration,
        desktopAdUnits: [...desktopOutOfRoadblock, ...desktopRoadblock],
        mobileAdUnits: [...mobileOutOfRoadblock, ...mobileRoadblock],
        refreshRules,
        lazyLoadingRuleConfig,
        // Stupid ChiplistInput
        floorRuleConfig: stack.configuration.floorRuleConfig
          ? {
              standardFloorRuleId: stack.configuration.floorRuleConfig.standardFloorRuleId,
              specificFloorRuleConfigs: stack.configuration.floorRuleConfig.specificFloorRuleConfigs?.map((rule) => ({
                floorRuleId: rule.floorRuleId,
                adUnitIds: rule.adUnitIds.map((adUnitId) => adUnits.find((a) => a.id === adUnitId)?.name ?? ''),
              })),
            }
          : undefined,
        mobileFloorRuleConfig: stack.configuration.mobileFloorRuleConfig
          ? {
              standardFloorRuleId: stack.configuration.mobileFloorRuleConfig.standardFloorRuleId,
              specificFloorRuleConfigs: stack.configuration.mobileFloorRuleConfig.specificFloorRuleConfigs?.map((rule) => ({
                floorRuleId: rule.floorRuleId,
                adUnitIds: rule.adUnitIds.map((adUnitId) => adUnits.find((a) => a.id === adUnitId)?.name ?? ''),
              })),
            }
          : undefined,
      },
    }
  }
  throw new Error('Unsupported stack type')
}

export const StackEdit: FunctionComponent = () => {
  // Context
  const [searchParams, _] = useSearchParams()
  const { siteId, stackId } = useParams()
  const stackTemplateId = searchParams.get('template') ?? undefined
  const navigate = useNavigate()
  // Saving a stack is owner-only at the API. The steps still open and read — the ad units and rules a
  // stack carries are worth seeing whether or not this reader can change them.
  const { canEdit, disabledReason } = useScopePermissions()
  const scope = useAtomValue(currentScopeState)

  // API Hooks
  const { data: site, isLoading: isSiteLoading } = useSite(siteId ?? '', scope.id)
  const { data: stackTemplate, isLoading: isStackTemplateLoading } = useStack(stackTemplateId)
  const { data: stack, isLoading: isStackLoading } = useStack(stackId)
  // Includes archived groups (they can be unarchived) so name-uniqueness accounts for them.
  const { data: siteStacks, isLoading: isStacksLoading } = useStacks({ siteId, type: 'adm', status: ['to-deploy', 'live', 'archived_group'] })
  const existingStacks = useMemo(() => siteStacks.filter((stack) => stack.status !== 'archived_group'), [siteStacks])
  const { mutateAsync: createStack, isPending: isStackCreationPending } = useCreateStack()
  const { mutateAsync: bulkUpdateStacks, isPending: isStackUpdatePending } = useBulkUpdateStacks()
  const { data: selectableAdUnits, isLoading: isAdUnitsLoading } = useAdUnits()
  const { data: contexts, isLoading: isContextsLoading } = useContexts()
  const { data: bidderRules, isLoading: isBidderRulesLoading } = useBidderRules('adm')
  const { data: lazyLoadingRules, isLoading: isLazyLoadingRulesLoading } = useLazyLoadingRules()
  const { data: refreshRules, isLoading: isRefreshRulesLoading } = useRefreshRules()

  const breadcrumbs = useBreadcrumbs(site ?? { name: '' })
  const isLoading = useMemo(
    () =>
      isStackLoading ||
      isStacksLoading ||
      isStackTemplateLoading ||
      isAdUnitsLoading ||
      isContextsLoading ||
      isSiteLoading ||
      isBidderRulesLoading ||
      isLazyLoadingRulesLoading ||
      isRefreshRulesLoading,
    [isStackLoading, isStacksLoading, isStackTemplateLoading, isAdUnitsLoading, isContextsLoading, isSiteLoading, isBidderRulesLoading, isLazyLoadingRulesLoading, isRefreshRulesLoading]
  )
  const isEditing = !!stackId
  const formSteps = 3
  const [currentStep, stepHelpers] = useCustomStep(formSteps)
  const toast = useToast()
  const formMethods = useForm<StackFormData>({
    defaultValues: {
      name: '',
      commitMessage: '',
      configuration: {
        headerBiddingEnabled: true,
        adCallTimeout: 3000,
        desktopAdUnits: [],
        mobileAdUnits: [],
      },
    },
    mode: 'onChange',
  })
  const {
    control,
    trigger,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
  } = formMethods

  const { name } = useWatch({ control })
  const desktopAdUnitsIds = (getValues('configuration.desktopAdUnits') ?? []).map((a) => a.adUnitId)
  const mobileAdUnitsIds = (getValues('configuration.mobileAdUnits') ?? []).map((a) => a.adUnitId)

  const contextOptions: SelectOptionProp[] = contexts
    .filter((c) => c.enabled)
    .map((context) => ({
      value: context.id,
      label: context.name,
      trackingLabel: 'adm-sites-stacks-form-context-option-set',
      // A context already taken by another stack of this media is not selectable — at most one stack
      // per context. The stack being edited is not "another": its own context stays available.
      // (The callback parameter used to be named `stack`, shadowing the edited stack above, so this
      // compared a variable to itself and never disabled anything.)
      disabled: !!existingStacks?.find((existingStack) => existingStack.configuration.contextId === context.id && existingStack.stackGroupId !== stack?.stackGroupId),
    }))

  const onContinue = async () => {
    const valid = await trigger(['configuration.desktopAdUnits', 'configuration.mobileAdUnits', 'name', 'configuration.contextId'])
    if (valid) {
      if (currentStep === 1) {
        updateRules()
      }
      stepHelpers.goToNextStep()
    } else {
      errors.configuration?.desktopAdUnits?.message && errors.configuration.desktopAdUnits?.type === 'validate' && toast.alert(errors.configuration.desktopAdUnits.message)
      errors.configuration?.mobileAdUnits?.message && errors.configuration.mobileAdUnits?.type === 'validate' && toast.alert(errors.configuration.mobileAdUnits.message)
    }
  }

  const onSubmit = async (form: StackFormData) => {
    if (isStackCreationPending || isStackUpdatePending) return
    const fillCorrespondingAdUnitsIdsArray = (roadblockIds: string[], outOfRoadblockIds: string[], adUnitConfig: AdUnitConfig) => {
      if (adUnitConfig.roadblock) {
        // the roadblock's master is always the first array's element
        adUnitConfig.roadblock === RoadblockType.Master ? roadblockIds.unshift(adUnitConfig.adUnitId) : roadblockIds.push(adUnitConfig.adUnitId)
      } else {
        outOfRoadblockIds.push(adUnitConfig.adUnitId)
      }
    }
    const desktopAdUnitsIds: string[] = []
    const desktopRoadblockAdUnitsIds: string[] = []
    form.configuration.desktopAdUnits.forEach((adUnitConfig) => fillCorrespondingAdUnitsIdsArray(desktopRoadblockAdUnitsIds, desktopAdUnitsIds, adUnitConfig))
    const mobileAdUnitsIds: string[] = []
    const mobileRoadblockAdUnitsIds: string[] = []
    form.configuration.mobileAdUnits.forEach((adUnitConfig) => fillCorrespondingAdUnitsIdsArray(mobileRoadblockAdUnitsIds, mobileAdUnitsIds, adUnitConfig))

    // The rules of step 3 hold ad unit names. A name that no longer matches an ad unit is dropped
    // here rather than saved as an empty id, whatever route through the form got us this far.
    const adUnitIdsByName = new Map(selectableAdUnits.map((adUnit) => [adUnit.name, adUnit.id]))

    const createAdmStack: CreateAdmStack = {
      ...form,
      configuration: {
        desktopAdUnitsIds,
        desktopRoadblockAdUnitsIds,
        mobileAdUnitsIds,
        mobileRoadblockAdUnitsIds,
        refreshRuleConfig: form.configuration.refreshRules?.flatMap((rule) =>
          resolveAdUnitIds(rule.adUnitIds, adUnitIdsByName).map((adUnitId) => ({
            device: rule.device,
            ruleId: rule.ruleId === 'NO_RULE' ? undefined : rule.ruleId,
            adUnitId,
          }))
        ),
        lazyLoadingRuleConfig: form.configuration.lazyLoadingRuleConfig?.every((rule) => rule.ruleId === 'NO_RULE')
          ? []
          : form.configuration.lazyLoadingRuleConfig?.map((rule) => ({ device: rule.device, ruleId: rule.ruleId === 'NO_RULE' ? undefined : rule.ruleId })),
        bidderRuleId: form.configuration.bidderRuleId,
        // The reserved "Default" stack has no context; the backend schema accepts string | null
        // (not undefined), so normalize a missing context to null.
        contextId: form.configuration.contextId ?? null,
        headerBiddingEnabled: form.configuration.headerBiddingEnabled,
        adCallTimeout: form.configuration.adCallTimeout,
        // So painful. We need to fix this ChiplistInput one day
        floorRuleConfig: form.configuration.floorRuleConfig ? resolveFloorRuleConfig(form.configuration.floorRuleConfig, adUnitIdsByName) : undefined,
        mobileFloorRuleConfig: form.configuration.mobileFloorRuleConfig ? resolveFloorRuleConfig(form.configuration.mobileFloorRuleConfig, adUnitIdsByName) : undefined,
      },
      siteId: siteId ?? '',
      type: 'adm',
      stackGroupId: stack?.stackGroupId || undefined,
      // The Default stack has no priority (it's always shown last); everyone else gets the next free slot.
      priority: isStackNameReserved(form.name) ? undefined : stack?.priority || existingStacks.reduce((max, { priority }) => (!!priority && priority > max ? priority : max), 0) + 1,
    }

    try {
      const createdStack = await createStack(createAdmStack)

      // We need to archive the previous version if it was "Ready to deploy".
      // Use the lightweight bulk update (id/status/ratio only) instead of a full PUT: re-sending the
      // whole stack would re-validate its stored snapshot/output, which can legitimately contain nulls.
      if (!!stack && stack.status === 'to-deploy') {
        await bulkUpdateStacks([{ ...toBulkUpdate(stack), status: 'archived', ratio: 0 }])
      }
      toast.success(
        <>
          <strong>
            {createdStack.name} - v{createdStack.version}
          </strong>{' '}
          is ready to be deployed.
        </>
      )
      navigate(`/monetization/one/sites/${siteId}/stacks`)
    } catch (error) {
      toast.alert(error.description.message)
    }
  }

  // The rules of step 3 are picked per ad unit. Once the ad units of step 1 change, every rule that
  // still points at a removed one has to be pruned, or the stack is saved with a stale ad unit.
  const updateRules = () => {
    const adUnitsById = selectableAdUnits.reduce<{ [key: string]: AdUnit }>((acc, adunit) => ({ ...acc, [adunit.id]: adunit }), {})
    // WARNING: it's not ids... don't get fooled by the name...
    const namesOf = (ids: string[]) => ids.map((id) => adUnitsById[id]?.name).filter((name): name is string => !!name)
    const adUnitNames = {
      desktop: namesOf((getValues('configuration.desktopAdUnits') ?? []).map((a) => a.adUnitId)),
      mobile: namesOf((getValues('configuration.mobileAdUnits') ?? []).map((a) => a.adUnitId)),
    }

    const refreshRules = getValues('configuration.refreshRules')
    if (refreshRules) {
      setValue('configuration.refreshRules', pruneRefreshRulesToAdUnits(refreshRules, adUnitNames))
    }
    const floorRuleConfig = getValues('configuration.floorRuleConfig')
    if (floorRuleConfig) {
      setValue('configuration.floorRuleConfig', pruneFloorRuleConfigToAdUnits(floorRuleConfig, adUnitNames.desktop))
    }
    const mobileFloorRuleConfig = getValues('configuration.mobileFloorRuleConfig')
    if (mobileFloorRuleConfig) {
      setValue('configuration.mobileFloorRuleConfig', pruneFloorRuleConfigToAdUnits(mobileFloorRuleConfig, adUnitNames.mobile))
    }
  }

  const validateStackUniqueName = async (name: string) => {
    if (name === '') return true
    // Stack names are unique per site: check against the site's existing (to-deploy/live) stacks locally.
    const normalizedName = name.trim().toLowerCase()
    return !siteStacks.some((stack) => stack.name.trim().toLowerCase() === normalizedName)
  }

  useEffect(() => {
    if (stack && !isAdUnitsLoading) {
      reset(fromStackToForm(stack, selectableAdUnits))
    }
  }, [stack, selectableAdUnits, isAdUnitsLoading])

  useEffect(() => {
    if (stackTemplate && !isStacksLoading && !isAdUnitsLoading) {
      reset({
        ...fromStackToForm(stackTemplate, selectableAdUnits),
        name: existingStacks.length === 0 ? 'Default' : '',
      })
    }
  }, [stackTemplate, existingStacks, isStacksLoading, selectableAdUnits, isAdUnitsLoading])

  // New stack created without a template: the first stack of a site is the reserved "Default" stack
  // (locked name + context), while any later stack starts with an empty, user-chosen name.
  useEffect(() => {
    if (!stackId && !stackTemplateId && !isStacksLoading) {
      setValue('name', existingStacks.length === 0 ? 'Default' : '')
    }
  }, [stackId, stackTemplateId, isStacksLoading, existingStacks.length, setValue])

  // New stack created without a template: preselect the default "All active bidders" bidder rule.
  useEffect(() => {
    if (!stackId && !stackTemplateId && !isBidderRulesLoading) {
      const defaultRule = bidderRules.find((rule) => isDefaultBidderRuleFor(rule, 'adm'))
      if (defaultRule) {
        setValue('configuration.bidderRuleId', defaultRule.id)
      }
    }
  }, [stackId, stackTemplateId, isBidderRulesLoading, bidderRules, setValue])

  return (
    <EditPage
      title={
        <div className={'flex flex-row flex-nowrap'}>
          <h2 className={'m-0 inline-flex flex-1 items-center font-rubik text-xl/[30px] font-normal text-jet'}>
            {isLoading ? <Skeleton bigger width={'120px'} /> : isEditing ? `Edit stack: ${stack?.name}` : `New stack${name ? `: ${name}` : ''}`}
          </h2>
          <StepperIndicator steps={formSteps} color={Colors.Lilac} currentStep={currentStep} />
        </div>
      }
      breadcrumbs={breadcrumbs}
      color={Colors.Lilac}
      onBackClick={() => navigate(-1)}
    >
      <FormProvider {...formMethods}>
        <form id={'stackForm'}>
          <div className={'py-5'}>
            {currentStep === 1 && (
              <PureStackAdUnitSelectionStep
                selectableAdUnits={selectableAdUnits}
                contextOptions={contextOptions}
                stackName={stack?.name ?? (existingStacks.length === 0 ? 'Default' : '')}
                validateUniqueName={validateStackUniqueName}
                isEditing={isEditing}
                isLoading={isLoading}
              />
            )}
            {currentStep === 2 && <PureStackHeaderBiddingRulesStep selectableHeaderBiddingRules={bidderRules} />}

            {currentStep === 3 && (
              <PureStackRulesStep
                selectableAdUnits={selectableAdUnits}
                selectableLazyLoadingRules={lazyLoadingRules}
                selectableRefreshRules={refreshRules}
                site={site}
                selectedAdUnitsIds={[...desktopAdUnitsIds, ...mobileAdUnitsIds]}
                isEditing={isEditing}
                isLoading={isLoading}
                stack={stack && isAdmStack(stack) ? stack : undefined}
              />
            )}
          </div>
          <div className={'flex flex-row justify-end gap-5'}>
            {stepHelpers.canGoToPrevStep && (
              <Button className={'mr-auto'} variant={'tertiary'} onClick={stepHelpers.goToPrevStep} trackingLabel={`adm-sites-stacks-form-${currentStep}-previous`}>
                Previous
              </Button>
            )}
            <Button variant={'tertiary'} onClick={() => navigate(-1)} trackingLabel={`adm-sites-stacks-form-${currentStep}-cancel`}>
              Cancel
            </Button>
            {stepHelpers.canGoToNextStep ? (
              <Button variant={'primary'} onClick={onContinue} disabled={isLoading} trackingLabel={`adm-sites-stacks-form-step-${currentStep}-next`}>
                Continue
              </Button>
            ) : (
              <Tooltip title={disabledReason} disabled={canEdit}>
                <Button variant={'primary'} disabled={!canEdit || isStackCreationPending || isLoading} onClick={handleSubmit((stack) => onSubmit(stack))} trackingLabel={`adm-sites-stacks-form-save`}>
                  Validate
                </Button>
              </Tooltip>
            )}
          </div>
        </form>
      </FormProvider>
    </EditPage>
  )
}
